import { datadogRum } from '@datadog/browser-rum-slim';
import { CoreApplicationContext } from '@eventbrite/context-gen';
import { EBEnvs, getCurrentEnv } from '@eventbrite/feature-detection';
import { RenderGlobalNavOrUndefined } from '@eventbrite/global-nav';
import { gettext } from '@eventbrite/i18n';
import { configureStore } from '@eventbrite/redux-configure-store';
import { middleware as analyticsMiddleware } from '@eventbrite/site-analytics';
import React, { useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import thunk from 'redux-thunk';
import { ConfirmationOverlay } from '../../components/ConfirmationOverlay';
import { ConsumerHeader } from '../../components/ConsumerHeader';
import reducer from '../../redux/reducers';
import { getInitialData } from '../../redux/reducers/utils';
import { ExperimentMap, FeatureFlags, FooterLinksShape } from '../../types';
import { ApiSearchData } from '../../types/Search';
import '../app.scss';
import { BasePage } from '../base';
import { SearchPageContent } from './SearchPageContent';
import { useInitialEventsExpansion } from './SearchPageDataExpansions';
import { SearchPageProviders } from './SearchPageProviders';

type SearchPageProps = RouteComponentProps & {
    env: CoreApplicationContext['env'];
    app_version?: string;
    app_name: string;
    user: {
        isAuthenticated?: boolean;
        publicId?: string;
        email?: string;
        isStaff?: boolean;
    };
    experiments: ExperimentMap;
    request: { session_id: string };
    breadcrumbs?: Array<{ title: string; [key: string]: any }>;
    search_data: ApiSearchData;
    location: Location;
    featureFlags?: FeatureFlags;
    footerLinks: FooterLinksShape;
    currentPlace: string;
};

const getInitialEventsIds = (searchData: ApiSearchData) => {
    const {
        events: { results = [], top_match = [] },
    } = searchData;

    const events = [...results, ...top_match];
    const eventIds = events.map(({ id }) => id);
    return eventIds;
};

const SEARCH_EVENTS = gettext('Search events');
const SEARCH = gettext('Search');

function SearchPage(props: SearchPageProps) {
    const {
        env,
        location,
        search_data,
        request,
        user,
        featureFlags,
        footerLinks,
        currentPlace,
    } = props;

    //TODO: Search Page Hierarchy experiment cleanup EB-228109
    const searchQuery = search_data.event_search.q || '';
    const desktopLabel = searchQuery || SEARCH_EVENTS;
    const mobileLabel = searchQuery || SEARCH;

    const searchBar = {
        renderSearchBar: true,
        desktopLabel,
        mobileLabel,
        location: currentPlace,
    };

    const GlobalNavHeader = RenderGlobalNavOrUndefined({
        config: {
            env,
            user,
            searchBar: searchBar,
        },
        featureFlag: featureFlags?.enableIaGlobalNav ?? false,
    });

    // TODO: Adjust dependencies of these memoizations
    // getInitialData requires a lot of props that are not currently being mapped
    // Sadly, using props as key is not valid, since it changes reference with every route change
    const initialState = useMemo(
        () => getInitialData(props),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const store = useMemo(
        () =>
            configureStore({
                reducer,
                initialState,
                middleware: [thunk, analyticsMiddleware(props)],
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    useDataDogEffects(env);

    const eventIds = getInitialEventsIds(search_data);

    useInitialEventsExpansion(eventIds, initialState.search.eventSearch);

    return (
        <SearchPageProviders
            store={store}
            location={location}
            experiments={search_data.experiment_enrollment}
        >
            <BasePage
                env={env}
                user={user}
                request={request}
                featureFlags={featureFlags}
                footerLinks={footerLinks}
                structureHeader={ConsumerHeader}
                overrideHeader={GlobalNavHeader}
                isSearchPage={true}
                removeOverflowSettings
                searchBar={searchBar}
            >
                <ConfirmationOverlay />
                <div className="feed-search-page">
                    <SearchPageContent />
                </div>
            </BasePage>
        </SearchPageProviders>
    );
}

function useDataDogEffects(env: CoreApplicationContext['env']) {
    const isProdEnv = getCurrentEnv({ env }) === EBEnvs.PROD;

    useEffect(() => {
        if (!isProdEnv) {
            return;
        }
        datadogRum.onReady(() =>
            datadogRum.addRumGlobalContext('view.sub_app', 'search'),
        );
    }, [isProdEnv]);
}

export default SearchPage;
